import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface StatTooltipProps {
  children: React.ReactNode;
  content: {
    title: string;
    description: string;
    details?: string[];
  };
  className?: string;
}

const StatTooltip: React.FC<StatTooltipProps> = ({ children, content, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        triggerRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsVisible(false);
    }
  };

  const handleClick = () => {
    if (isMobile) {
      setIsVisible(!isVisible);
    }
  };

  // On phones the card is fixed and centred. It is portalled to <body>
  // because the stats grid keeps a `transform` after its entrance animation,
  // and a transformed ancestor traps position: fixed.
  const tooltip = (
    <div
      ref={tooltipRef}
      id="tooltip"
      className={`absolute z-50 ${isMobile ? 'w-52 max-w-xs p-4' : 'w-80 max-w-sm p-4'} bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-2xl tooltip-enter ${isMobile ? 'stat-tooltip-mobile' : ''}`}
      style={{
        bottom: isMobile ? 'auto' : '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: isMobile ? '0' : '12px',
      }}
    >
      {/* Arrow pointing down */}
      <div className="stat-tooltip-arrow absolute top-full left-1/2 transform -translate-x-1/2">
        <div className="border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-800/95"></div>
      </div>

      <div className="text-left">
        <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} font-semibold text-white ${isMobile ? 'mb-1' : 'mb-2'}`}>
          {content.title}
        </h3>
        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-300 ${isMobile ? 'mb-2' : 'mb-3'} leading-relaxed`}>
          {content.description}
        </p>
        
        {content.details && content.details.length > 0 && (
          <ul className={`${isMobile ? 'space-y-0' : 'space-y-1'}`}>
            {content.details.map((detail, index) => (
              <li key={index} className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-400 flex items-start`}>
                <span className={`text-primary ${isMobile ? 'mr-1' : 'mr-2'}`}>•</span>
                <span>{detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      <div
        ref={triggerRef}
        className="cursor-pointer transition-[transform,color] duration-200 ease-out hover:scale-[1.03] active:scale-[0.98] hover:text-primary/90"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-describedby={isVisible ? 'tooltip' : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsVisible(!isVisible);
          }
          if (e.key === 'Escape') {
            setIsVisible(false);
          }
        }}
      >
        {children}
      </div>

      {isVisible && (isMobile
        ? ReactDOM.createPortal(
            <>
              <div
                aria-hidden="true"
                className="backdrop-enter"
                style={{ position: 'fixed', inset: 0, zIndex: 49, background: 'rgba(0,0,0,0.35)' }}
              />
              {tooltip}
            </>,
            document.body
          )
        : tooltip)}

    </div>
  );
};

export default StatTooltip;